import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { useLocation, useNavigate, Outlet } from 'react-router'
import { useLanguage } from '../hooks/useLanguage'
import UnicityLogoWhite from './assets/UnicityLogo-White.svg?react'
import UnicityLogoNavy from './assets/UnicityLogo-Navy.svg?react'
import FacebookIcon from './assets/FacebookIcon.svg?react'
import InstagramIcon from './assets/InstagramIcon.svg?react'
import YoutubeIcon from './assets/YoutubeIcon.svg?react'
import XIcon from './assets/XIcon.svg?react'
import GoToTopIcon from './assets/GoToTopIcon.svg?react'
import { Fragment } from 'react/jsx-runtime'

const socialMedia = [
    {
        icon: <FacebookIcon />,
        url: 'https://www.facebook.com/UnicityInternational/'
    },
    {
        icon: <YoutubeIcon />,
        url: 'https://www.youtube.com/channel/UCAiAqkbresXIqIQhT3Lfq7A'
    },
    {
        icon: <InstagramIcon />,
        url: 'https://www.instagram.com/unicityinternational/'
    },
    {
        icon: <XIcon />,
        url: 'https://x.com/Unicity'
    }
]

const MobileLayout = () => {
    const location = useLocation()
    const { availableLanguages, selectedLanguage, changeLanguage, currentLanguage, hasMultipleLanguages } = useLanguage()
    const navigate = useNavigate()
    const isHomePage = location.pathname === '/'

    const handleLanguageChange = (event: SelectChangeEvent) => {
        changeLanguage(event.target.value as string)
    }

    // Get display name for current language
    const getLanguageDisplayName = () => {
        return currentLanguage?.name || 'English'
    }

    return (
        <Stack
            sx={{
                minHeight: '100vh',
                height: '100%',
                maxWidth: '430px',
                margin: '0 auto',
                bgcolor: '#EDF3F8',
                boxShadow: { xs: 'none', sm: '0 0 20px rgba(0,0,0,0.1)' },
                display: 'flex',
                flexDirection: 'column'
            }}>
            {/* Header */}
            <Stack
                component="header"
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    top: 0,
                    zIndex: 100,
                    height: '65px',
                    px: 2,
                    bgcolor: 'white'
                }}>
                {/* Left: Back Button or Logo */}
                <Box sx={{ minWidth: 80, height: '20px' }} onClick={() => navigate('/')}>
                    <UnicityLogoNavy />
                </Box>

                {/* Right: Language Selector */}
                <Stack direction="row" alignItems="center" gap={1}>
                    {hasMultipleLanguages ? (
                        <Select
                            value={selectedLanguage}
                            onChange={handleLanguageChange}
                            size="small"
                            variant="standard"
                            disableUnderline
                            IconComponent={() => <></>}
                            sx={{
                                color: '#003B6F',
                                '& .MuiSelect-select': {
                                    py: 0,
                                    pr: '0px !important',
                                    fontSize: '18px'
                                },
                                '& .MuiSelect-icon': {
                                    color: '#003B6F',
                                    right: 0
                                }
                            }}
                            MenuProps={{
                                slotProps: {
                                    list: {
                                        sx: {
                                            '&::-webkit-scrollbar': {
                                                width: 8
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                borderRadius: 8,
                                                backgroundColor: 'rgba(0,0,0,0.3)'
                                            }
                                        }
                                    },
                                    paper: {
                                        sx: {
                                            mt: 4,
                                            width: '160px',
                                            maxHeight: '232px',
                                            overflow: 'scroll',
                                            scrollbarWidth: 'thin'
                                        }
                                    }
                                }
                            }}
                            renderValue={() => getLanguageDisplayName()}>
                            {availableLanguages.map(lang => (
                                <MenuItem
                                    key={lang.code}
                                    value={lang.code.toUpperCase()}
                                    sx={{
                                        fontSize: '16px',
                                        color: 'primary.main',
                                        paddingY: '0px',
                                        minHeight: '36px',
                                        bgcolor: lang.code.toUpperCase() === selectedLanguage ? 'warning.dark' : 'transparent',
                                        '&:hover': {
                                            bgcolor: lang.code.toUpperCase() === selectedLanguage ? 'warning.dark' : 'action.hover'
                                        },
                                        '&.Mui-selected': {
                                            bgcolor: 'warning.dark',
                                            '&:hover': {
                                                bgcolor: 'warning.dark'
                                            }
                                        }
                                    }}>
                                    {lang.name}
                                </MenuItem>
                            ))}
                        </Select>
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#03274C'
                            }}>
                            {getLanguageDisplayName()}
                        </Typography>
                    )}
                </Stack>
            </Stack>

            {/* Main Content - flex: 1 ensures it takes remaining space */}
            <Box
                component="main"
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0 // Allow flex shrinking
                }}>
                <Outlet />
            </Box>

            {/* Footer - always at bottom */}
            <Stack
                sx={{
                    bgcolor: 'white',
                    flexShrink: 0 // Prevent footer from shrinking
                }}
                alignItems={'center'}
                gap={'32px'}
                component={'footer'}>
                {isHomePage && (
                    <Fragment>
                        <IconButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} sx={{ cursor: 'pointer', p: 0 }}>
                            <GoToTopIcon />
                        </IconButton>
                        <Stack direction={'row'} gap={4} justifyContent={'center'} alignItems={'center'}>
                            {socialMedia.map(item => (
                                <Box key={item.url} height={'35px'}>
                                    <a href={item.url} target="_blank" rel="noopener noreferrer">
                                        {item.icon}
                                    </a>
                                </Box>
                            ))}
                        </Stack>
                    </Fragment>
                )}
                <Stack height={'80px'} width={'100%'} bgcolor={'background1.main'} justifyContent={'center'} alignItems={'center'}>
                    <UnicityLogoWhite />
                </Stack>
            </Stack>
        </Stack>
    )
}

export default MobileLayout
